import { useRef } from 'react';
import { Navigate } from 'react-router-dom';
import { PlayerAvatar } from '../components/PlayerAvatar';
import { GlassPanel } from '../components/GlassPanel';
import { MaterialIcon } from '../components/MaterialIcon';
import { useGame } from '../state/gameContext';
import { getSortedPlayers } from '../state/mockGameReducer';

export default function GameOverPage() {
  const { state, playAgain, newRoom, reset } = useGame();

  // Capture validity once, at mount. The end-screen buttons (Play Again / New
  // Room) intentionally clear the game state while navigating to /create; if we
  // re-evaluated this guard on every render it would see the cleared state and
  // redirect to "/", overriding the button's own navigation. Direct visits with
  // no game state still mount invalid and get redirected as expected.
  const wasValidOnMount = useRef(Boolean(state.roomCode) && state.phase === 'gameOver');

  if (!wasValidOnMount.current) {
    return <Navigate to="/" replace />;
  }

  const sorted = getSortedPlayers(state);
  const podium = sorted.slice(0, 3);
  const rest = sorted.slice(3);

  return (
    <div className="bg-background text-on-background min-h-screen font-body-lg overflow-x-hidden">
      <header className="fixed top-0 w-full z-50 flex justify-center items-center h-16 pointer-events-none">
        <button
          type="button"
          onClick={reset}
          className="font-headline-md text-headline-md font-black text-primary italic tracking-tighter pointer-events-auto"
        >
          Deck After Dark
        </button>
      </header>

      <main className="relative z-10 pt-20 pb-32 px-margin-mobile md:px-margin-desktop max-w-7xl mx-auto flex flex-col items-center">
        <div className="text-center mb-stack-lg md:mb-16">
          <h1 className="font-display-lg text-display-lg text-primary glow-primary uppercase tracking-widest mb-stack-xs" data-testid="game-over-title">
            Game Over!
          </h1>
          {state.endedReason === 'notEnoughPlayers' ? (
            <p
              className="font-headline-md-mobile text-headline-md-mobile text-on-surface-variant font-medium"
              data-testid="game-ended-reason"
            >
              Game ended because not enough players remain.
            </p>
          ) : (
            <p className="font-headline-md-mobile text-headline-md-mobile text-on-surface-variant font-medium">
              {podium[0]?.name} takes the night with {podium[0]?.score} points.
            </p>
          )}
        </div>

        <div className="flex flex-col md:flex-row items-end justify-center gap-6 mb-16 w-full max-w-4xl">
          {podium.map((player, index) => {
            const rank = index + 1;
            const order = rank === 1 ? 'order-1 md:order-2 z-20' : rank === 2 ? 'order-2 md:order-1' : 'order-3';
            return (
              <div key={player.id} className={`w-full md:w-1/3 flex flex-col items-center ${order}`}>
                <div className="relative mb-stack-sm animate-float">
                  {rank === 1 && (
                    <div className="absolute -top-12 left-1/2 -translate-x-1/2 text-primary glow-primary">
                      <MaterialIcon name="military_tech" className="text-6xl" filled />
                    </div>
                  )}
                  <PlayerAvatar
                    initials={player.initials}
                    gradient={player.gradient}
                    size="lg"
                    alt={`${player.name} podium avatar`}
                    ring={rank === 1 ? 'border-4 border-primary glow-primary' : 'border-4 border-surface-variant'}
                    className={rank === 1 ? 'w-32 h-32' : 'w-20 h-20'}
                  />
                  <div className="absolute -bottom-2 -right-2 bg-primary text-on-primary font-bold rounded-full flex items-center justify-center border-2 border-background w-8 h-8">
                    {rank}
                  </div>
                </div>
                <GlassPanel className="w-full pt-8 pb-6 px-4 rounded-t-xl text-center flex flex-col items-center">
                  <span className={`mb-1 ${rank === 1 ? 'font-headline-md text-headline-md text-primary' : 'font-label-bold text-label-bold text-on-surface'}`}>
                    {player.name}
                  </span>
                  <span className={`font-bold text-2xl ${rank === 1 ? 'text-primary font-black text-3xl' : rank === 2 ? 'text-secondary' : 'text-tertiary'}`}>
                    {player.score} pts
                  </span>
                </GlassPanel>
              </div>
            );
          })}
        </div>

        {rest.length > 0 && (
          <GlassPanel className="w-full max-w-2xl p-stack-md mb-16 overflow-hidden">
            <h3 className="font-label-bold text-label-bold text-on-surface-variant uppercase tracking-widest px-4 mb-4">
              The Rest of the Pack
            </h3>
            <div className="space-y-2">
              {rest.map((player, i) => (
                <div key={player.id} className="flex items-center gap-4 px-4 py-3 rounded-xl hover:bg-white/5 transition-colors">
                  <span className="font-label-bold text-on-surface-variant w-4">{i + 4}</span>
                  <PlayerAvatar initials={player.initials} gradient={player.gradient} size="md" alt={`${player.name} avatar`} />
                  <div className="flex-1">
                    <p className="font-label-bold text-on-surface">{player.name}</p>
                  </div>
                  <div className="text-on-surface-variant font-mono">{player.score} pts</div>
                </div>
              ))}
            </div>
          </GlassPanel>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 w-full max-w-4xl">
          <button
            type="button"
            onClick={playAgain}
            data-testid="play-again"
            className="bg-primary hover:bg-primary-container text-on-primary-container font-headline-md-mobile h-16 rounded-xl transition-all flex items-center justify-center gap-3 active:scale-95 glow-primary group"
          >
            <MaterialIcon name="replay" className="group-hover:rotate-180 transition-transform duration-500" />
            Play Again
          </button>
          <button
            type="button"
            onClick={newRoom}
            data-testid="new-room"
            className="bg-surface-container-highest hover:bg-surface-bright text-on-surface font-label-bold h-16 rounded-xl transition-all flex items-center justify-center gap-3 border border-white/10 active:scale-95"
          >
            <MaterialIcon name="add_circle" />
            New Room
          </button>
          <button
            type="button"
            onClick={reset}
            data-testid="back-home"
            className="bg-surface-container-highest hover:bg-surface-bright text-on-surface font-label-bold h-16 rounded-xl transition-all flex items-center justify-center gap-3 border border-white/10 active:scale-95"
          >
            <MaterialIcon name="home" />
            Back Home
          </button>
          <button
            type="button"
            data-testid="share-results"
            title="Sharing is not part of the demo build yet."
            className="border-2 border-secondary hover:bg-secondary/10 text-secondary font-label-bold h-16 rounded-xl transition-all flex items-center justify-center gap-3 active:scale-95 glow-secondary"
          >
            <MaterialIcon name="share" />
            Share Results
          </button>
        </div>
      </main>
    </div>
  );
}
